import React, { useState, useEffect } from 'react';
import { FileText, Download, Search, Filter, Calendar, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { TransactionSkeleton } from '../../components/shared/ClientPageSkeleton';

const ClientTransactions = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [transactions] = useState([
    {
      id: 1,
      type: 'Payment',
      description: 'Deposit for Wedding Photography - Photo Studio Pro',
      amount: 750.00,
      status: 'completed',
      date: '2024-12-15',
      transactionId: 'TXN-2024-001'
    },
    {
      id: 2,
      type: 'Payment',
      description: 'Platform Fee - Corporate Event Booking',
      amount: 100.00,
      status: 'completed',
      date: '2024-12-10',
      transactionId: 'TXN-2024-002'
    },
    {
      id: 3,
      type: 'Refund',
      description: 'Refund for Cancelled Booking - Event Capture Co',
      amount: -500.00,
      status: 'completed',
      date: '2024-11-25',
      transactionId: 'TXN-2024-003'
    },
    {
      id: 4,
      type: 'Payment',
      description: 'Final Payment - Wedding Photography',
      amount: 750.00,
      status: 'pending',
      date: '2024-12-20',
      transactionId: 'TXN-2024-004'
    }
  ]);

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.transactionId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || transaction.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <XCircle className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const totalAmount = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);

  if (isLoading) {
    return (
      <ClientPageLayout title="Transaction History">
        <div className="bg-white rounded-lg border border-black p-4 sm:p-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-8 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="h-10 flex-1 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
          <TransactionSkeleton count={5} />
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout title="Transaction History">
      <div className="bg-white rounded-lg border border-black p-4 sm:p-6">
        {/* Summary Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
              <p className="text-2xl font-bold text-black">{filteredTransactions.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-black">
                ${Math.abs(totalAmount).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Transactions List */}
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-bold text-black mb-2">No Transactions Found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-base text-black">
                          {transaction.type}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(transaction.status)}`}>
                          {getStatusIcon(transaction.status)}
                          <span className="ml-1 capitalize">{transaction.status}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {transaction.description}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3.5 h-3.5 mr-1" />
                          <span>{new Date(transaction.date).toLocaleDateString()}</span>
                        </div>
                        <span>ID: {transaction.transactionId}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 sm:ml-4">
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        transaction.amount >= 0 ? 'text-black' : 'text-red-600'
                      }`}>
                        {transaction.amount >= 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                      </p>
                    </div>
                    <button
                      className="p-2 text-gray-400 hover:text-black transition-colors"
                      title="Download receipt"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ClientPageLayout>
  );
};

export default ClientTransactions;

